import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
  created_at: string;
}

function api(path: string, opts?: RequestInit) {
  const token = localStorage.getItem("kverna-token");
  return fetch(path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...opts?.headers,
    },
  });
}

export function Team() {
  const { t } = useTranslation();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const load = async () => {
    const res = await api("/api/team");
    if (res.ok) {
      setMembers(await res.json());
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const res = await api("/api/team", {
      method: "POST",
      body: JSON.stringify({ name, email, password }),
    });

    if (res.ok) {
      setShowInvite(false);
      setName("");
      setEmail("");
      setPassword("");
      await load();
    } else {
      const err = await res.json();
      setError(err.error);
    }
  };

  const handleToggleActive = async (userId: string, active: boolean) => {
    await api(`/api/team/${userId}/active`, {
      method: "PUT",
      body: JSON.stringify({ active }),
    });
    await load();
  };

  if (loading) {
    return <div className="text-center py-16 text-navy-400">{t("common.loading")}</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "var(--font-display)" }}>
            {t("team.title")}
          </h1>
          <p className="text-navy-500 text-sm">{t("team.subtitle")}</p>
        </div>
        <button onClick={() => setShowInvite(true)} className="btn btn-primary">
          {t("team.invite")}
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 mb-6 text-sm">
          {error}
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <div className="bg-white rounded-xl border border-navy-100 p-6 mb-6">
          <h2 className="font-semibold mb-4" style={{ fontFamily: "var(--font-display)" }}>
            {t("team.inviteTitle")}
          </h2>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-navy-500 mb-1">{t("team.name")}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-navy-500 mb-1">{t("team.email")}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input w-full"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-navy-500 mb-1">{t("team.tempPassword")}</label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input w-full"
                minLength={8}
                required
              />
              <p className="text-xs text-navy-400 mt-1">{t("team.passwordNote")}</p>
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn btn-primary">{t("team.sendInvite")}</button>
              <button type="button" onClick={() => setShowInvite(false)} className="btn btn-ghost">
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team list */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-navy-400 text-xs border-b border-navy-100 bg-navy-50/50">
              <th className="px-6 py-3 font-medium">{t("team.name")}</th>
              <th className="px-6 py-3 font-medium">{t("team.email")}</th>
              <th className="px-6 py-3 font-medium">{t("team.role")}</th>
              <th className="px-6 py-3 font-medium">{t("team.status")}</th>
              <th className="px-6 py-3 font-medium">{t("team.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-navy-50">
                <td className="px-6 py-3 font-medium">{m.name}</td>
                <td className="px-6 py-3 text-navy-500">{m.email}</td>
                <td className="px-6 py-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      m.role === "owner" ? "bg-amber-50 text-amber-600" : "bg-sky-50 text-sky-600"
                    }`}
                  >
                    {m.role}
                  </span>
                </td>
                <td className="px-6 py-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      m.active ? "bg-emerald-50 text-emerald-600" : "bg-navy-100 text-navy-400"
                    }`}
                  >
                    {m.active ? t("team.active") : t("team.inactive")}
                  </span>
                </td>
                <td className="px-6 py-3">
                  {m.role === "technician" && (
                    <button
                      onClick={() => handleToggleActive(m.id, !m.active)}
                      className={`text-xs ${m.active ? "text-rose-500 hover:text-rose-600" : "text-emerald-500 hover:text-emerald-600"}`}
                    >
                      {m.active ? t("team.deactivate") : t("team.activate")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
